export type MethodologySourceId = 'nolan' | 'eseb' | 'wvs' | 'latinobarometro' | 'cses';

export interface MethodologySource {
  id: MethodologySourceId;
  name: string;
  description: string;
  url?: string;
}

export type MatchPoliticalAxis = 'liberdade-economica' | 'liberdade-pessoal';

export type MatchPoliticalDirection = 1 | -1;

export interface PoliticalMatchQuestion {
  id: string;
  question: string;
  theme: string;
  axis: MatchPoliticalAxis;
  direction: MatchPoliticalDirection;
  weight: number;
  methodologySourceIds: MethodologySourceId[];
  isQuickQuestion?: boolean;
}

export const questionnaireVersion = '2026.1';

export const methodologyLastUpdated = '2026-08-11';

export const methodologySources: MethodologySource[] = [
  {
    id: 'nolan',
    name: 'Diagrama de Nolan',
    description: 'Base conceitual para separar liberdade econômica e liberdade pessoal em dois eixos.',
  },
  {
    id: 'eseb',
    name: 'ESEB / CESOP-Unicamp',
    description: 'Referência brasileira para economia, redistribuição, valores políticos e democracia.',
  },
  {
    id: 'wvs',
    name: 'World Values Survey',
    description: 'Referência internacional para valores sociais, autoridade, religião, liberdade e papel do Estado.',
  },
  {
    id: 'latinobarometro',
    name: 'Latinobarómetro',
    description: 'Referência latino-americana para direitos civis, liberdade de expressão, segurança e economia.',
  },
  {
    id: 'cses',
    name: 'CSES',
    description: 'Referência comparada para surveys políticos, minorias, direitos individuais e desenho de escalas.',
  },
];

export const politicalMatchQuestions: PoliticalMatchQuestion[] = [
  {
    id: 'economica-impostos-gastos-menores',
    question: 'O governo deveria cobrar menos imposto e gastar menos, mesmo se alguns serviços piorarem.',
    theme: 'Impostos e gasto público',
    axis: 'liberdade-economica',
    direction: 1,
    weight: 1.5,
    methodologySourceIds: ['nolan', 'eseb', 'wvs'],
    isQuickQuestion: true,
  },
  {
    id: 'economica-privatizacao-servicos',
    question: 'Serviços como limpeza, lixo e transporte poderiam ser feitos por empresa privada.',
    theme: 'Serviços do dia a dia',
    axis: 'liberdade-economica',
    direction: 1,
    weight: 1.3,
    methodologySourceIds: ['nolan', 'eseb'],
    isQuickQuestion: true,
  },
  {
    id: 'economica-negociacao-trabalhista',
    question: 'Empresas e trabalhadores deveriam poder combinar salário e regras com mais liberdade.',
    theme: 'Leis do trabalho',
    axis: 'liberdade-economica',
    direction: 1,
    weight: 1.2,
    methodologySourceIds: ['eseb', 'wvs', 'nolan'],
    isQuickQuestion: true,
  },
  {
    id: 'economica-regulacao-negocios',
    question: 'O governo deveria colocar menos papelada para abrir e manter um negócio.',
    theme: 'Regras para negócios',
    axis: 'liberdade-economica',
    direction: 1,
    weight: 1.4,
    methodologySourceIds: ['nolan', 'eseb', 'wvs'],
    isQuickQuestion: true,
  },
  {
    id: 'economica-redistribuicao-renda',
    question: 'O governo deveria ajudar mais quem ganha pouco, mesmo com mais imposto.',
    theme: 'Ajuda para quem ganha pouco',
    axis: 'liberdade-economica',
    direction: -1,
    weight: 1.5,
    methodologySourceIds: ['eseb', 'wvs', 'latinobarometro'],
  },
  {
    id: 'economica-servicos-publicos-ampliados',
    question: 'O governo deveria gastar mais em saúde e escola, mesmo que isso pese no bolso.',
    theme: 'Saúde e escola',
    axis: 'liberdade-economica',
    direction: -1,
    weight: 1.3,
    methodologySourceIds: ['eseb', 'wvs', 'latinobarometro'],
  },
  {
    id: 'economica-subsidios-direcionamento',
    question: 'O governo deveria dar incentivo para empresas e setores que geram emprego.',
    theme: 'Incentivos do governo',
    axis: 'liberdade-economica',
    direction: -1,
    weight: 1,
    methodologySourceIds: ['eseb', 'wvs'],
  },
  {
    id: 'economica-gestao-privada-melhor',
    question: 'O governo deveria manter empresas do Estado em áreas importantes.',
    theme: 'Empresas do Estado',
    axis: 'liberdade-economica',
    direction: -1,
    weight: 1.2,
    methodologySourceIds: ['nolan', 'eseb'],
  },
  {
    id: 'pessoal-expressao-restricoes',
    question: 'O governo deveria poder barrar fala ofensiva.',
    theme: 'Fala e opinião',
    axis: 'liberdade-pessoal',
    direction: -1,
    weight: 1.4,
    methodologySourceIds: ['nolan', 'wvs', 'latinobarometro'],
    isQuickQuestion: true,
  },
  {
    id: 'pessoal-privacidade-vigilancia',
    question: 'O governo deveria ter menos acesso aos dados e mensagens das pessoas.',
    theme: 'Dados e privacidade',
    axis: 'liberdade-pessoal',
    direction: 1,
    weight: 1.5,
    methodologySourceIds: ['nolan', 'wvs', 'cses'],
    isQuickQuestion: true,
  },
  {
    id: 'pessoal-autonomia-vida-privada',
    question: 'Cada pessoa deveria poder viver do seu jeito sem o governo mandar.',
    theme: 'Vida pessoal',
    axis: 'liberdade-pessoal',
    direction: 1,
    weight: 1.3,
    methodologySourceIds: ['nolan', 'wvs', 'latinobarometro'],
    isQuickQuestion: true,
  },
  {
    id: 'pessoal-religiao-estado',
    question: 'Religião não deveria mandar nas decisões do governo.',
    theme: 'Religião e governo',
    axis: 'liberdade-pessoal',
    direction: 1,
    weight: 1.2,
    methodologySourceIds: ['wvs', 'latinobarometro'],
    isQuickQuestion: true,
  },
  {
    id: 'pessoal-seguranca-monitoramento',
    question: 'Em nome da segurança, o governo pode vigiar mais as pessoas.',
    theme: 'Segurança e vigilância',
    axis: 'liberdade-pessoal',
    direction: -1,
    weight: 1.4,
    methodologySourceIds: ['nolan', 'wvs', 'cses'],
  },
  {
    id: 'pessoal-maioria-nao-pode-impor',
    question: 'A maioria não deveria impor seu jeito de viver aos outros.',
    theme: 'Maioria e costumes',
    axis: 'liberdade-pessoal',
    direction: 1,
    weight: 1,
    methodologySourceIds: ['wvs', 'latinobarometro'],
  },
  {
    id: 'pessoal-moral-tradicional-punicao',
    question: 'O governo não deveria punir hábitos privados que não machucam ninguém.',
    theme: 'Hábitos privados',
    axis: 'liberdade-pessoal',
    direction: -1,
    weight: 1.2,
    methodologySourceIds: ['wvs', 'cses', 'latinobarometro'],
  },
  {
    id: 'pessoal-minorias-protecao',
    question: 'Direitos de grupos pequenos devem valer mesmo quando a maioria reclama.',
    theme: 'Direitos de grupos menores',
    axis: 'liberdade-pessoal',
    direction: 1,
    weight: 1.5,
    methodologySourceIds: ['cses', 'wvs', 'latinobarometro'],
  },
  {
    id: 'pessoal-descriminalizacao-drogas',
    question: 'Drogas leves deveriam ser tratadas como saúde, não como cadeia.',
    theme: 'Drogas e saúde',
    axis: 'liberdade-pessoal',
    direction: 1,
    weight: 1.3,
    methodologySourceIds: ['wvs', 'latinobarometro', 'cses'],
  },
  {
    id: 'pessoal-protestos-ordem-publica',
    question: 'O governo deveria poder parar protestos que travam a cidade.',
    theme: 'Protestos e ordem',
    axis: 'liberdade-pessoal',
    direction: -1,
    weight: 1.1,
    methodologySourceIds: ['cses', 'latinobarometro'],
  },
  {
    id: 'pessoal-remocao-conteudo-politico',
    question: 'O governo deveria poder tirar posts políticos das redes.',
    theme: 'Redes sociais',
    axis: 'liberdade-pessoal',
    direction: -1,
    weight: 1.2,
    methodologySourceIds: ['cses', 'latinobarometro', 'wvs'],
  },
  {
    id: 'pessoal-justica-menor-intervencao',
    question: 'A justiça deveria se meter menos em brigas e escolhas de adultos.',
    theme: 'Justiça e escolhas',
    axis: 'liberdade-pessoal',
    direction: 1,
    weight: 1.1,
    methodologySourceIds: ['wvs', 'cses'],
  },
  {
    id: 'pessoal-valores-morais-escola',
    question: 'A escola pública deveria ensinar valores morais escolhidos pelo governo.',
    theme: 'Escola e valores',
    axis: 'liberdade-pessoal',
    direction: -1,
    weight: 1,
    methodologySourceIds: ['wvs', 'eseb', 'latinobarometro'],
  },
  {
    id: 'pessoal-liberdade-associacao',
    question: 'As pessoas deveriam poder se juntar em grupos sem o governo travar.',
    theme: 'Livre associação',
    axis: 'liberdade-pessoal',
    direction: 1,
    weight: 1.2,
    methodologySourceIds: ['wvs', 'cses', 'nolan'],
  },
  {
    id: 'pessoal-saude-em-vez-de-punicao',
    question: 'Quem usa drogas deveria receber tratamento, não só punição.',
    theme: 'Drogas e tratamento',
    axis: 'liberdade-pessoal',
    direction: 1,
    weight: 1.4,
    methodologySourceIds: ['wvs', 'latinobarometro', 'cses'],
  },
  {
    id: 'pessoal-limites-comportamento-privado',
    question: 'O governo deveria poder entrar na casa das pessoas quando achar necessário.',
    theme: 'Casa e governo',
    axis: 'liberdade-pessoal',
    direction: -1,
    weight: 1,
    methodologySourceIds: ['wvs', 'nolan', 'eseb'],
  },
];

export const methodologySourceById = methodologySources.reduce<Record<MethodologySourceId, MethodologySource>>(
  (accumulator, source) => {
    accumulator[source.id] = source;
    return accumulator;
  },
  {} as Record<MethodologySourceId, MethodologySource>,
);
